import { useEffect, useRef } from "react";
import { useWatch, type Path, type UseFormSetValue } from "react-hook-form";
import { useQuery } from "@tanstack/react-query";
import {
  FormSection,
  ControlledInput,
  ControlledComboBox,
} from "@excent-one/react/form";
import { FormFieldSet } from "@progress/kendo-react-all";
import { withMemoizedContainer } from "@excent-one/react/utils/children";
import {
  searchMemberBasicInfo,
  getMemberDetails,
} from "@phrmpc/infrastructure/phrmpc/api/members";
import type { IncidentFormSectionProps, IncidentFormValues } from "../types";

export interface InvolvedMemberSectionProps extends IncidentFormSectionProps {
  title: string; // "Person to be Reported" | "Reported By"
  setValue: UseFormSetValue<IncidentFormValues>;
  idFieldName: Path<IncidentFormValues>; // "reportedMemberId" | "reporterMemberId"
  departmentFieldName: Path<IncidentFormValues>;
  positionFieldName: Path<IncidentFormValues>;
  clientFieldName: Path<IncidentFormValues>;
}

/**
 * Renders one member-lookup section (used twice: "Person to be Reported" and "Reported By") -
 * a combobox bound to the member id field, plus Department (editable)/Position/Client
 * (read-only, auto-populated from the selected member's details). Fully self-sufficient: fetches
 * its own member list and member-details lookup rather than receiving them as props, since
 * nothing else on the page needs this data (see the "sections own their data" principle in the
 * IncidentForm.tsx refactor plan). Instantiated twice with different field-name props; both
 * instances share one `searchMemberBasicInfo` network call via React Query's query-key dedup.
 */
const InvolvedMemberSection = withMemoizedContainer(
  FormSection,
  (
    {
      control,
      setValue,
      readOnly,
      loading,
      idFieldName,
      departmentFieldName,
      positionFieldName,
      clientFieldName,
      ...sectionProps
    }: InvolvedMemberSectionProps,
    Container,
  ) => {
    const memberId = useWatch({ control, name: idFieldName });

    const { data: memberOptions } = useQuery({
      queryKey: ["members-basic"],
      queryFn: async () => {
        const result = await searchMemberBasicInfo({
          payload: { pageNumber: 1, pageSize: 500 },
        });
        return result.items ?? [];
      },
    });

    const { data: memberDetails } = useQuery({
      queryKey: ["member-details", memberId],
      queryFn: () => getMemberDetails(memberId as string),
      enabled: Boolean(memberId),
    });

    // Position/Client are now real DTO fields (Bug 157809/157610/157601's fix, PR 2850)
    // snapshotted server-side on Create/Update, so the initial mount already has the correct
    // persisted values via initialValues - skip the very first run here to avoid clobbering
    // them with a live re-fetch before it resolves. Only re-resolve when the user actively picks
    // a different member afterward.
    const skipNextAutoPopulate = useRef(true);
    useEffect(() => {
      if (skipNextAutoPopulate.current) {
        skipNextAutoPopulate.current = false;
        return;
      }
      setValue(positionFieldName, memberDetails?.positionName ?? "");
      setValue(clientFieldName, memberDetails?.clientName ?? "");
    }, [memberDetails, positionFieldName, clientFieldName, setValue]);

    return (
      <Container loading={loading} {...sectionProps}>
        <FormFieldSet cols={2}>
          {memberOptions && (
            <ControlledComboBox
              name={idFieldName}
              control={control}
              loading={loading}
              label="Name"
              data={memberOptions}
              dataItemKey="id"
              textField="fieldName"
              readOnly={readOnly}
              required={!readOnly}
              onChange={() => setValue(departmentFieldName, "")}
            />
          )}
          <ControlledInput
            name={departmentFieldName}
            control={control}
            loading={loading}
            label="Department"
            readOnly={readOnly}
            required={!readOnly}
          />
          <ControlledInput
            name={positionFieldName}
            control={control}
            loading={loading}
            label="Position"
            readOnly
          />
          <ControlledInput
            name={clientFieldName}
            control={control}
            loading={loading}
            label="Client"
            readOnly
          />
        </FormFieldSet>
      </Container>
    );
  },
);

export default InvolvedMemberSection;
